import {
    DEFAULT_INACTIVITY_ALERT_DELAY_CLUSTER_HMI,
    DEFAULT_INACTIVITY_LIMIT_CLUSTER_HMI,
    FAILUREDETAILSSTEPS,
} from "constants/constants";
import { HMICachedDataContext } from "contexts/HMICachedDataContext/HMICachedDataContext";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { MachineCenterContext } from "contexts/MachineCenterContext/MachineCenterContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

const useAutoNavigateClusterHMI = (handleMovingToCluster: () => Promise<void>, isClusterHMI: boolean) => {
    const { machineStatus } = useContext(MachineCenterContext);
    const { failureDetailsStep } = useContext(HMIContext);
    const { store } = useContext(HMICachedDataContext);

    const inactivityAlertDelay = useMemo(
        () => store.machineCenter?.cluster?.inactivity_alert_delay || DEFAULT_INACTIVITY_ALERT_DELAY_CLUSTER_HMI,
        [store.machineCenter],
    );
    const inactivityTimeLimit = useMemo(
        () => store.machineCenter?.cluster?.inactivity_time_limit || DEFAULT_INACTIVITY_LIMIT_CLUSTER_HMI,
        [store.machineCenter],
    );
    const totalTime = useMemo(
        () => inactivityTimeLimit + inactivityAlertDelay,
        [inactivityTimeLimit, inactivityAlertDelay],
    );

    const [timeRemaining, setTimeRemaining] = useState<number>(totalTime);

    const resetTimer = useCallback(() => {
        setTimeRemaining(totalTime);
    }, [totalTime]);

    useEffect(() => {
        let interval: ReturnType<typeof setTimeout> | undefined = undefined;
        const downtimeWizardSteps =
            failureDetailsStep > FAILUREDETAILSSTEPS.FAILUREDETAILSSELECTIONS ||
            failureDetailsStep === FAILUREDETAILSSTEPS.FAILURETYPES;
        if ((machineStatus || downtimeWizardSteps) && isClusterHMI) {
            if (timeRemaining > 0) {
                interval = setInterval(() => {
                    setTimeRemaining((prevTime) => {
                        const newTime = prevTime - 1;
                        return newTime;
                    });
                }, 1000);
            } else if (timeRemaining === 0) {
                handleMovingToCluster();
            }

            return () => clearInterval(interval);
        } else {
            resetTimer();
        }
    }, [timeRemaining, handleMovingToCluster, isClusterHMI, resetTimer, machineStatus, failureDetailsStep]);

    useEffect(() => {
        const handleGlobalClick = () => {
            resetTimer();
        };

        if (isClusterHMI) {
            window.addEventListener("click", handleGlobalClick);

            return () => {
                window.removeEventListener("click", handleGlobalClick);
            };
        }
    }, [resetTimer, isClusterHMI]);

    const shouldShowTimer = useMemo(() => timeRemaining <= inactivityTimeLimit, [timeRemaining, inactivityTimeLimit]);

    return {
        timeRemaining,
        resetTimer,
        shouldShowTimer,
    };
};

export default useAutoNavigateClusterHMI;
